import { createAsyncThunk } from "@reduxjs/toolkit";
import { davApiService } from "../../helpers/dav_api_helper";
import { TokenManagerHelper } from "../../helpers/token_manager_helper";
import { RootState } from "../index";
import {
  dashboardLoadError,
  dashboardLoadStart,
  dashboardLoadSuccess,
  updateChartData,
} from "./reducer";

import { setCustomers } from "../customers/reducer";

import { setPaymentForms } from "../paymentForms/reducer";

import { setPaymentMethods } from "../paymentMethods/reducer";

import { setProducts } from "../products/reducer";

import { loginSuccess } from "../auth/login/reducer";
import { getOrders } from "../orders/thunk";

export const recalculateChartData = createAsyncThunk(
  "dashboard/recalculateChartData",
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const ordersFromSlice = state.Orders.orders;

    const totalOrders = ordersFromSlice.length;

    const pendingOrders = ordersFromSlice.filter((order) => order.status === 1).length;

    const syncedOrders = ordersFromSlice.filter((order) => order.status === 2).length;

    const currentDate = new Date();
    const monthlyOrdersData = Array.from({ length: 12 }, (_, index) => {
      const month = new Date(currentDate.getFullYear(), currentDate.getMonth() - (11 - index), 1);
      return ordersFromSlice.filter((order) => {
        const orderDate = new Date(order.dateEmission);
        return (
          orderDate.getMonth() === month.getMonth() &&
          orderDate.getFullYear() === month.getFullYear()
        );
      }).length;
    });

    dispatch(
      updateChartData({
        monthlyOrders: monthlyOrdersData,
        totalOrders,
        pendingOrders,
        syncedOrders,
      })
    );

    return {
      totalOrders,
      pendingOrders,
      syncedOrders,
      monthlyOrdersData,
    };
  }
);

export const loadDashboardData = createAsyncThunk(
  "dashboard/loadData",
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const token = TokenManagerHelper.getToken();
      if (!token) {
        console.error("❌ Dashboard: Token não encontrado");
        return rejectWithValue("Token de acesso não encontrado");
      }

      const userDataStr = localStorage.getItem("authUser");
      if (!userDataStr) {
        console.error("❌ Dashboard: Dados do usuário não encontrados no localStorage");
        return rejectWithValue("Dados do usuário não encontrados");
      }

      const userData = JSON.parse(userDataStr);
      const documentOwner = userData.documentOwner;
      if (!documentOwner) {
        console.error("❌ Dashboard: documentOwner não encontrado nos dados do usuário");
        return rejectWithValue("Identificador do proprietário não encontrado");
      }

      dispatch(dashboardLoadStart());

      const response = await davApiService.fetchDavData(documentOwner, token);

      if (response.user) {
        dispatch(
          loginSuccess({
            user: response.user,
            token: token,
          })
        );
      }

      localStorage.removeItem("users");
      localStorage.removeItem("orders");
      localStorage.removeItem("customers");
      localStorage.removeItem("paymentForms");
      localStorage.removeItem("paymentMethods");
      localStorage.removeItem("products");

      if (response.customers) {
        dispatch(setCustomers(response.customers));
        localStorage.setItem("customers", JSON.stringify(response.customers));
      }

      if (response.paymentForms) {
        dispatch(setPaymentForms(response.paymentForms));
        localStorage.setItem("paymentForms", JSON.stringify(response.paymentForms));
      }

      if (response.paymentMethods) {
        dispatch(setPaymentMethods(response.paymentMethods));
        localStorage.setItem("paymentMethods", JSON.stringify(response.paymentMethods));
      }

      if (response.products) {
        dispatch(setProducts(response.products));
        localStorage.setItem("products", JSON.stringify(response.products));
      }

      // Carregar pedidos se não existirem
      const state = getState() as RootState;
      let ordersFromSlice = state.Orders.orders;

      if (ordersFromSlice.length === 0) {
        await dispatch(getOrders());
        const updatedState = getState() as RootState;
        ordersFromSlice = updatedState.Orders.orders;
      }

      const totalOrders = ordersFromSlice.length;

      const pendingOrders = ordersFromSlice.filter((order) => order.status === 1).length;

      const syncedOrders = ordersFromSlice.filter((order) => order.status === 2).length;

      const currentDate = new Date();
      const monthlyOrdersData = Array.from({ length: 12 }, (_, index) => {
        const month = new Date(currentDate.getFullYear(), currentDate.getMonth() - (11 - index), 1);
        return ordersFromSlice.filter((order) => {
          const orderDate = new Date(order.dateEmission);
          return (
            orderDate.getMonth() === month.getMonth() &&
            orderDate.getFullYear() === month.getFullYear()
          );
        }).length;
      });

      dispatch(
        updateChartData({
          monthlyOrders: monthlyOrdersData,
          totalOrders,
          pendingOrders,
          syncedOrders,
        })
      );

      dispatch(dashboardLoadSuccess());

      return response;
    } catch (error: unknown) {
      let errorMessage = "Erro desconhecido ao carregar dados";

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === "object" && error !== null && "response" in error) {
        const axiosError = error as { response?: { data?: { message?: string } } };
        errorMessage = axiosError.response?.data?.message || errorMessage;
      }

      console.error("❌ Dashboard: Erro na requisição:", error);
      console.error("❌ Dashboard: Mensagem de erro:", errorMessage);

      dispatch(dashboardLoadError(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);
